import { FileOutlined, FileFilled as FilledFileIcon } from "@ant-design/icons"
import moment from "moment"
import styles from "./utils.module.css"

const HEX_WITHOUT_HASH = /^[0-9a-fA-F]{3,8}$/

export const getEventColor = (experienceColor) => {
  const normalizedColor = typeof experienceColor === "string" ? experienceColor.trim() : experienceColor
  if (!normalizedColor) return "var(--color-primary-surface)"
  if (typeof normalizedColor === "string" && HEX_WITHOUT_HASH.test(normalizedColor)) return `#${normalizedColor}`
  return normalizedColor
}

export const getEventBorderColor = () => "var(--color-primary)"

export const getJournalIcon = (journalNoteDates, date) => {
  const hasJournal = journalNoteDates?.includes(date)
  return hasJournal ? (
    <FilledFileIcon className={styles.journalIcon} />
  ) : (
    <FileOutlined className={styles.journalIcon} />
  )
}

export const startTimeValidate = (t, endTime, currentValue) => {
  if (!currentValue || !endTime) return Promise.resolve()
  if (currentValue.isBefore(endTime)) return Promise.resolve()
  return Promise.reject(new Error(t("calendar.schedule_time_off.start_time_must_be_before_end_time")))
}

export const endTimeValidate = (t, startTime, currentValue) => {
  if (!currentValue || !startTime) return Promise.resolve()
  if (currentValue.isAfter(startTime)) return Promise.resolve()
  return Promise.reject(new Error(t("calendar.schedule_time_off.end_time_must_be_after_start_time")))
}

export const startDateValidate = (t, endDate, currentValue) => {
  if (!currentValue || !endDate) return Promise.resolve()
  // Allow same day or before
  if (currentValue.isBefore(endDate) || currentValue.isSame(endDate, "day")) return Promise.resolve()
  return Promise.reject(new Error(t("calendar.schedule_time_off.start_date_must_be_before_end_date")))
}

export const endDateValidate = (t, startDate, currentValue) => {
  if (!currentValue || !startDate) return Promise.resolve()
  // Allow same day or after
  if (currentValue.isAfter(startDate) || currentValue.isSame(startDate, "day")) return Promise.resolve()
  return Promise.reject(new Error(t("calendar.schedule_time_off.end_date_must_be_after_start_date")))
}

export const formatDate = (date) => date?.format("YYYY-MM-DD")
export const formatTime = (time) => time?.format("hh:mm a")

export const capitalizeFirstLetter = (str) => str?.charAt(0)?.toUpperCase() + str?.slice(1)

export const parseTime = (t) => (t ? moment(t, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null)

export const overlaps = (aStart, aEnd, bStart, bEnd) => {
  if (!aStart || !aEnd || !bStart || !bEnd) return false
  return aStart.isBefore(bEnd) && aEnd.isAfter(bStart)
}

export const parseTimeOffRanges = (timeOffRanges = []) => {
  return timeOffRanges.map((range) => {
    const [startStr, endStr] = String(range).split(" - ")
    return { start: parseTime(startStr), end: parseTime(endStr) }
  })
}

// Normalize event start/end times to display labels
export const formatEventLabels = (ev) => {
  const startMoment = parseTime(ev.start)
  const endMoment = parseTime(ev.end)
  return {
    ...ev,
    startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
    endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end,
  }
}

// Common helpers for all views
export const DAY_KEY_FORMAT = "YYYY-MM-DD"
export const dayKey = (m) => (m ? m.format(DAY_KEY_FORMAT) : "")

// Build a per-day map of time off information
// Returns shape: { [dayKey]: { allDay: boolean, ranges: [{ start, end }] } }
export const normalizeTimeOffByDay = (calendarTimeOff = [], calendarDays = []) => {
  const map = {}
  calendarDays.forEach((day) => {
    const key = dayKey(day)
    const items = (calendarTimeOff || [])
      .filter((toff) => moment(toff.day_date, ["M/D/YYYY", "YYYY-MM-DD"]).isSame(day, "day"))
      .map((toff) => {
        const start = moment(toff.start_time, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"])
        const end = moment(toff.end_time, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"])
        return { ...toff, start, end }
      })
    map[key] = {
      allDay: items.some((toff) => Boolean(toff.all_day)),
      ranges: items.map(({ start, end }) => ({ start, end }))
    }
  })
  return map
}

// Determine if an event conflicts with time off for a given day
export const hasEventConflict = (startMoment, endMoment, timeOffForDay) => {
  if (!startMoment || !endMoment || !timeOffForDay) return false
  if (timeOffForDay.allDay) return true
  const ranges = timeOffForDay.ranges || []
  return ranges.some((r) => overlaps(startMoment, endMoment, r.start, r.end))
}

const tabToViewType = (tab) => {
  if (tab === "my-bookings") return "my_bookings"
  if (tab === "crew") return "crew"
  if (tab === "equipment") return "equipment"
  return null
}

const getTabUnassigned = (tab, savedFilters) => {
  if (tab === "crew") return Boolean(savedFilters.crew_unassigned)
  if (tab === "equipment") return Boolean(savedFilters.equipment_unassigned)
  return false
}

export const buildBookingsParams = (params, dates, savedFilters) => {
  const apiParams = new URLSearchParams()
  apiParams.set("start_date", dates.start)
  apiParams.set("end_date", dates.end)

  const viewType = tabToViewType(params.tab)
  if (viewType) apiParams.set("view_type", viewType)

  if (savedFilters.search) apiParams.set("search", savedFilters.search)

  if (params.tab === "all-bookings") {
    savedFilters.experience_ids?.forEach((id) => apiParams.append("experience_ids[]", id))
    savedFilters.boat_ids?.forEach((id) => apiParams.append("boat_ids[]", id))
    savedFilters.profile_ids?.forEach((id) => apiParams.append("profile_ids[]", id))
    if (savedFilters.crew_unassigned || savedFilters.equipment_unassigned) apiParams.set("unassigned", "true")
  }
  if (params.tab === "my-bookings") {
    savedFilters.experience_ids?.forEach((id) => apiParams.append("experience_ids[]", id))
    savedFilters.boat_ids?.forEach((id) => apiParams.append("boat_ids[]", id))
  }
  if (params.tab === "equipment") {
    const equipmentUnassigned = getTabUnassigned(params.tab, savedFilters)
    const hasBoatFilters = savedFilters.boat_ids?.length > 0
    const hasCategoryFilters = savedFilters.category_ids?.length > 0
    
    if (equipmentUnassigned && !hasBoatFilters) {
      apiParams.set("unassigned", "true")
    } else if (hasBoatFilters || hasCategoryFilters) {
      savedFilters.boat_ids?.forEach((id) => apiParams.append("boat_ids[]", id))
      savedFilters.category_ids?.forEach((id) => apiParams.append("category_ids[]", id))
      if (equipmentUnassigned) apiParams.set("unassigned", "true")
    }
  }

  if (params.tab === "crew") {
    const crewUnassigned = getTabUnassigned(params.tab, savedFilters)
    const hasProfileFilters = savedFilters.profile_ids?.length > 0
    const hasRoleFilters = savedFilters.role_ids?.length > 0
    
    if (crewUnassigned && !hasProfileFilters) {
      apiParams.set("unassigned", "true")
    } else if (hasProfileFilters || hasRoleFilters) {
      savedFilters.profile_ids?.forEach((id) => apiParams.append("profile_ids[]", id))
      savedFilters.role_ids?.forEach((id) => apiParams.append("role_ids[]", id))
      if (crewUnassigned) apiParams.set("unassigned", "true")
    }
  }
  return apiParams
}

/**
 * Params for `GET /guide/blackouts/calendar` — the day chips, scoped the way the bookings under
 * them are.
 *
 * The feed used to take the date window alone, so the Equipment tab pinned to one boat still
 * showed "Crew OFF (1)": the rows were filtered, the chip above them was not, and it read as if
 * the crew closure applied to that boat.
 *
 * The scoping is the server's call, not ours. Deciding whether a crew record touches a given
 * boat means walking the boat→asset cascade and the resource→listing cascade, which only the
 * backend computes — the same reason `blackout_conflict` moved server-side after the client's
 * match-by-title stopgap proved wrong. So this sends the tab and its resource filters and reads
 * whatever comes back.
 *
 * Business and listing blackouts are never scoped out: they close the day for every boat and
 * every crew member, so they belong on every tab. That rule lives on the server too — this
 * function says nothing about scope, only which resources the guide is currently looking at.
 */
export const buildBlackoutsParams = (params, dates, savedFilters = {}) => {
  const apiParams = new URLSearchParams()
  apiParams.set("start_date", dates.start)
  apiParams.set("end_date", dates.end)

  const viewType = tabToViewType(params.tab)
  // Only the resource tabs narrow the feed. "All Bookings" and "My Bookings" are listing-level
  // views where every closure is relevant, so they ask for the unscoped picture.
  if (viewType !== "crew" && viewType !== "equipment") return apiParams

  apiParams.set("view_type", viewType)

  if (viewType === "equipment") {
    savedFilters.boat_ids?.forEach((id) => apiParams.append("boat_ids[]", id))
    savedFilters.category_ids?.forEach((id) => apiParams.append("category_ids[]", id))
  } else {
    savedFilters.profile_ids?.forEach((id) => apiParams.append("profile_ids[]", id))
    savedFilters.role_ids?.forEach((id) => apiParams.append("role_ids[]", id))
  }

  return apiParams
}

/** A multi-day booking is the same trip on every day it covers, so it is keyed once. */
const spanKey = (booking) => booking?.uuid || booking?.id

/**
 * Lays a week's multi-day bookings out as bars across its columns.
 *
 * A multi-day booking reaches the calendar once per day it covers (see `expandMultiDayBookings`
 * in `store/calendar`), which in a month grid would draw the same trip up to seven times. Each
 * run instead becomes one bar on its first day in the week, and every day it covers reserves the
 * same lane — so the single-day tiles underneath stay on one line across the whole row.
 *
 * Runs are packed into the fewest lanes that keep them from overlapping, longest first.
 *
 * @param {string[]} dayKeys the week's days in order, as "YYYY-MM-DD"
 * @param {Object.<string, Object[]>} bookingsByDay every day's bookings, multi-day ones included
 * @returns {{lanesByDay: Object.<string, Array>, laneCount: number}} `lanesByDay[dayKey][lane]` is
 *   `{ booking, span }` on the day the run starts, `{ booking, span: 0 }` on a day it passes over,
 *   and a hole where that lane belongs to another run.
 */
export const buildBookingSpans = (dayKeys = [], bookingsByDay = {}) => {
  const runs = new Map()

  dayKeys.forEach((dayKey, index) => {
    ;(bookingsByDay[dayKey] || []).forEach((booking) => {
      if (!booking?.multi_day) return
      const key = spanKey(booking)
      const run = runs.get(key)
      if (run) run.end = index
      else runs.set(key, { booking, start: index, end: index })
    })
  })

  // Longest first at equal starts, so a long trip takes the top lane and short ones tuck under it.
  const ordered = [...runs.values()].sort((left, right) => left.start - right.start || right.end - left.end)
  const laneEnds = []
  const lanesByDay = {}

  ordered.forEach((run) => {
    let lane = laneEnds.findIndex((lastIndex) => lastIndex < run.start)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(-1)
    }
    laneEnds[lane] = run.end

    for (let index = run.start; index <= run.end; index += 1) {
      const dayKey = dayKeys[index]
      lanesByDay[dayKey] = lanesByDay[dayKey] || []
      lanesByDay[dayKey][lane] = { booking: run.booking, span: index === run.start ? run.end - run.start + 1 : 0 }
    }
  })

  return { lanesByDay, laneCount: laneEnds.length }
}
